import React, { MouseEvent, useState } from "react";

import InputLabelWrapper from "components/basic/input-label-wrapper/InputLabelWrapper";
import { Maybe, MdmDeviceConfigDto, MdmDeviceConfigDtoInput, MeasurementMappingInput, useCreateUpdateDeviceConfigurationMutation, useDeleteDeviceConfigurationMutation } from "gql/generated/types";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";
import { useParams } from "react-router-dom";
import { Dialog } from "primereact/dialog";
import { Chips } from "primereact/chips";
import { InputNumber } from "primereact/inputnumber";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup";
import { convertTimeSpanToString, secondsToTimeSpan } from "utils/time-utils";

type DeviceConfigurationProps = {
  configurations:  Maybe<Maybe<MdmDeviceConfigDto>[]> | undefined
}

const DeviceConfiguration: React.FC<DeviceConfigurationProps> = ({ configurations }) => {
  const [title, setTitle] = useState("");
  const { projectId } = useParams();
  const [mutateDeviceConfig] = useCreateUpdateDeviceConfigurationMutation({
    refetchQueries: ["GetProject"]
  })

  const mappedConfigs = configurations?.map((config, index) => (
    <MeasurementMapping key={config?.id} config={config} index={index} />
  ));

  return (
    <div>
     {configurations?.length! > 0 && mappedConfigs}
     <div className="flex mt-3">
        <div className="p-inputgroup w-3">
          <InputText
            value={title}
            onChange={e => setTitle(e.target.value)}
            name="title"
            placeholder="Title for configuration"
          />
          <Button
            type="submit"
            icon="pi pi-plus"
            className="p-inputgroup-addon "
            title="Add new configuration"
            onClick={() => {
              mutateDeviceConfig({variables: { input: {title, projectId, measurementMappings: [] }}})
              setTitle("")
            }}
          />
        </div>
      </div>
    </div>
  )
}

export default DeviceConfiguration;

type MeasurementMappingProps = {
  config: Maybe<MdmDeviceConfigDto> | undefined
  index: number
}

const MeasurementMapping: React.FC<MeasurementMappingProps> = ({config, index}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [deleteDeviceConfig] = useDeleteDeviceConfigurationMutation({
    onCompleted: () => {
      easasAlert({
        severity: "success",
        summary: "Success",
        detail: "Device configuration deleted successfully",
        life: 3000,
      });
    },
    onError: err => {
      easasAlert({ severity: "error", summary: "Error", detail: err.message, life: 3000 });
    },
    refetchQueries: ["GetProject"]
  })

  const handleDeleteDeviceConfig = (event: MouseEvent<HTMLButtonElement>) => { 
    confirmPopup({
      target: event.currentTarget,
      message: "Are you sure you want to delete this device configuration?",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => {
        if (config) {
          deleteDeviceConfig({variables: {id: config?.id!}})
        }
      },
    });
  }

  return (
    <div key={config?.id} className="grid">
      <ConfirmPopup/>
      <InputLabelWrapper labelName={index === 0 ? "Title" : ""} className="gap-3" col={3}>
        <p>{config?.title}</p>
      </InputLabelWrapper>
      <InputLabelWrapper
        labelName={index === 0 ? "Vitality Threshold" : ""}
        className="gap-3"
        col={2}
      >
        <p>{config?.vitalityThreshold ? `${config?.vitalityThreshold} s` : "-"}</p>
      </InputLabelWrapper>
      <InputLabelWrapper
        labelName={index === 0 ? "Measurement Mappings" : ""}
        className="gap-3"
        col={5}
      >
        <ul className="pl-4 m-0">
          {config?.measurementMappings.map(mapper => (
            <li key={mapper?.key}>
              {mapper?.key} -{" "}
              {mapper?.value.map(chip => <Tag key={chip} value={chip} className="mx-1" title={chip ?? ""} />)}
            </li>
          ))}
        </ul>
      </InputLabelWrapper>
      <InputLabelWrapper labelName={index === 0 ? "Actions" : ""} className="gap-3" col={2}>
        <div className="flex flex-nowrap gap-1">
          <Button
            icon="pi pi-pencil"
            className="p-button-rounded p-button-text"
            onClick={() => setIsOpen(true)}
          />
          <Button
            icon="pi pi-trash"
            className="p-button-rounded p-button-text p-button-danger"
            onClick={handleDeleteDeviceConfig}
          />
        </div>
      </InputLabelWrapper>
      <Dialog
        visible={isOpen}
        onHide={() => setIsOpen(false)}
        header="Edit configuration"
        style={{ width: "700px" }}
      >
        <DeviceConfigurationForm configuration={config} closeDialog={() => setIsOpen(false)}/>
      </Dialog>
    </div>
  );
}

type DeviceConfigurationFormProps = {
  configuration: Maybe<MdmDeviceConfigDto> | undefined
  closeDialog: () => void
}

const DeviceConfigurationForm: React.FC<DeviceConfigurationFormProps> = ({ configuration, closeDialog }) => {
  const [config, setConfig] = useState(configuration);
  const { projectId } = useParams();
  const [mutateDeviceConfig] = useCreateUpdateDeviceConfigurationMutation({
    onCompleted: () => {
      easasAlert({
        severity: "success",
        summary: "Success",
        detail: "Device configuration updated successfully",
        life: 3000,
      });
      closeDialog();
    },
    onError: err => {
      easasAlert({ severity: "error", summary: "Error", detail: err.message, life: 3000 });
    },
    refetchQueries: ["GetProject"]
  });
  return (
    <div className="grid align-items-end">
      <InputLabelWrapper labelName="Title" className="gap-3">
        <InputText
          value={config?.title ?? ""}
          onChange={e => setConfig({ ...config!, title: e.target.value })}
          name="title"
          placeholder="Title for configuration"
        />
      </InputLabelWrapper>
      <InputLabelWrapper labelName="Vitality Threshold" className="gap-3">
        <div className="p-inputgroup">
          <InputNumber
            value={config?.vitalityThreshold}
            onChange={e => setConfig({ ...config!, vitalityThreshold: e.value })}
            name="vitalityThreshold"
            placeholder="sec"
            suffix=" s"
          />
          <span className="p-inputgroup-addon" title="Seconds to time">
            <span className="text-2xl mx-2">&#8658;</span>
            {convertTimeSpanToString(secondsToTimeSpan(config?.vitalityThreshold ?? 0))}
          </span>
        </div>
      </InputLabelWrapper>
      <InputLabelWrapper labelName="Measurement Mappings" className="gap-3" col={12}>
        {config?.measurementMappings.map((mapper, i) => (
          <div key={i} className="flex flex-row gap-3">
            <InputText
              value={mapper?.key ?? ""}
              onChange={e =>
                setConfig(prev => ({
                  ...prev,
                  measurementMappings: prev?.measurementMappings?.map((it, ind) =>
                    ind === i ? { ...it, key: e.target.value } : it
                  ) as Maybe<MeasurementMappingInput>[],
                }))
              }
              name="key"
              placeholder="Key"
            />
            <Chips
              value={mapper?.value ?? []}
              onChange={e =>
                setConfig(prev => ({
                  ...prev,
                  measurementMappings: prev?.measurementMappings?.map(it =>
                    it?.key === mapper?.key ? { ...it, value: e.value } : it
                  ) as Maybe<MeasurementMappingInput>[],
                }))
              }
              name="value"
              placeholder="Tags"
            />
            <Button
              title="Delete measurement"
              icon="pi pi-trash"
              className="p-button-rounded p-button-text p-button-danger"
              onClick={() =>
                setConfig(prev => ({
                  ...prev,
                  measurementMappings: prev?.measurementMappings.toSpliced(
                    i,
                    1
                  ) as Maybe<MeasurementMappingInput>[],
                }))
              }
            />
          </div>
        ))}
      </InputLabelWrapper>
      <div className="flex justify-content-between w-full">
        <Button
          title="Add measurement"
          icon="pi pi-plus"
          className="p-button-rounded p-button-text p-button-success"
          onClick={() =>
            setConfig({
              ...config,
              measurementMappings: [...(config?.measurementMappings ?? []), { key: "", value: [] }],
            })
          }
        />
        <Button
          label="Update"
          title="Save configuration"
          className="p-button-rounded mt-3"
          onClick={() => {
            mutateDeviceConfig({
              variables: { input: { ...config!, projectId } as MdmDeviceConfigDtoInput },
            });
          }}
        />
      </div>
    </div>
  );
}